import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {CartDecoratorErrorHandlingService} from '@app/checkout/services/cart-decorator-error-handling.service';
import {
  DcpEntitlementService,
  DcpGlobalAccount,
  DcpTenant,
  DcpTenantKey
} from '@app/checkout/services/dcp-entitlement.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CommercialModelConfig, CommercialModelMessage} from '@app/model/dcp-config-service/commercial-model-config';
import {MultiCurrencyErrorInfo} from '@app/model/multi-currency-cart-error-info.model';
import {ConfiguratorProductService} from '@app/product/configurator/services/configurator-product.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {DcpProductService} from '@app/product/services/dcp-product.service';
import {ProductConfigurationService} from '@app/product/services/product-configuration.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {ProductDetailsPageHistoryService} from '@app/services/user-registration/product-details-page-history.service';
import {
  ConfiguratorUriParameterService,
  DcpGlobalMessageService,
  DcpPriceData,
  FeatureTogglesService,
  ProductCodes,
  TransactionType
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {Product, TranslationService} from '@spartacus/core';
import {cloneDeep, isEqual} from 'lodash-es';
import {combineLatest, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {concatMap, debounceTime, distinctUntilChanged, filter, first, map,} from 'rxjs/operators';
import {DcpCartConfigurationForm} from '../../dcp-cart-configuration.model';
import {ValidateCartEntryService} from './validate-cart-entry.service';
import {CLOSE_EVENT, IFrameMessageService} from '@app/services/iframe/iframe-message.service';
import {TransactionTypeTexts} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';

export interface EditModeConfig {
  isInEditMode: boolean;
  containerClass: string;
  btnCssClass: string;
  btnTranslationKey: string;
  cypressSelector: string;
}

@UntilDestroy()
@Component({
  selector: 'dcp-edit-cart-entry',
  templateUrl: './edit-cart-entry.component.html',
  styleUrls: ['./edit-cart-entry.component.scss']
})
export class EditCartEntryComponent implements OnInit, OnDestroy {
  @Input() isModalOpened: boolean = false;
  @Output() multiCurrencyCartError: EventEmitter<MultiCurrencyErrorInfo> = new EventEmitter<MultiCurrencyErrorInfo>();
  @Output() addedToCart: EventEmitter<void> = new EventEmitter<void>();
  @Output() finishedLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() setHeader: EventEmitter<string> = new EventEmitter<string>();
  @Output() triggerClose: EventEmitter<void> = new EventEmitter<void>();

  reduceConfigureCallsFeatureToggle: boolean;
  variantProduct$: Observable<Product>;
  displayProduct$: Observable<Product>;
  commercialModelConfig: CommercialModelConfig;
  variantProductLoaded$: Observable<boolean>;
  displayProductLoaded$: Observable<boolean>;
  backToSolutionPage$: Observable<boolean>;
  nameAndSupplierAlternativeAid: string;
  renderedComponents: ComponentRef<any>[] = [];
  configuratorFields: FormlyFieldConfig[];
  configuratorForm: UntypedFormGroup = new UntypedFormGroup({});
  configuratorModel: DcpCartConfigurationForm;
  /** ugly solution to avoid race condition */
  isConfiguratorModelSet$: ReplaySubject<boolean> = new ReplaySubject(1);
  transactionTypeTexts$: Observable<TransactionTypeTexts>;
  priceData: DcpPriceData;
  priceData$: Subject<DcpPriceData> = new Subject<DcpPriceData>();
  transactionType: TransactionType;
  infoMessages: CommercialModelMessage[];
  configureCallsCount = 0;
  skipConfigureCall = false;
  heading: string;
  modalMode: string;
  forcedModal: boolean

  readonly DISPLAY_MODE = {
    DEFAULT: {
      isInEditMode: false,
      containerClass: 'col-12 col-sm-6',
      btnTranslationKey: 'addToCart.addToCart',
      btnCssClass: 'btn-outline-primary w-100',
      cypressSelector: 'add-to-cart-button',
    },
    EDIT: {
      isInEditMode: true,
      containerClass: 'col-12',
      btnCssClass: 'btn-primary w-100',
      btnTranslationKey: 'addToCart.updateCart',
      cypressSelector: 'update-cart-button',
    }
  };
  editModeConfig: EditModeConfig = this.DISPLAY_MODE.DEFAULT;
  formInitialized: boolean = false;

  entitlements$: Observable<{ tenants: DcpTenant[]; globalAccounts: DcpGlobalAccount[] }>;
  tenantKeys: DcpTenantKey[];

  processingState: boolean = false;
  isLoading: boolean = true;
  readonly CONFIGURE_CALL_AFTER_INIT = 2;
  private _currentCartEntry: OrderEntry;

  constructor(
    private cdr: ChangeDetectorRef,
    private productConfigurationService: ProductConfigurationService,
    private productUtils: DcpProductUtilitiesService,
    private translationService: TranslationService,
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private configuratorProductService: ConfiguratorProductService,
    private entitlementService: DcpEntitlementService,
    private route: ActivatedRoute,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private validateCartEntryService: ValidateCartEntryService,
    private dcpProductService: DcpProductService,
    private uriParamService: ConfiguratorUriParameterService,
    private transactionTypeService: TransactionTypeService,
    private dcpOrderEntryService: DcpOrderEntryService,
    private featureTogglesService: FeatureTogglesService,
    private dcpConfigService: DcpConfigService,
    private cartErrorHandlingService: CartDecoratorErrorHandlingService,
    private router: Router,
    private pdpHistoryService: ProductDetailsPageHistoryService,
    private messageService: IFrameMessageService,
  ) {
  }

  get cartEntry() {
    return this._currentCartEntry;
  }

  closeModal() {
    if (!this.modalMode && !this.forcedModal) {
      return;
    }

    if (this.forcedModal) {
      this.triggerClose.emit();
    } else {
      this.messageService.postMessage({
        eventId: CLOSE_EVENT,
      });
    }
  }

  @Input() set cartEntry(entry: OrderEntry) {
    if (entry?.product?.code) {
      this._currentCartEntry = entry;
      this.configuratorProductService.setExistingVariantProduct(entry.product);
    }
  }

  @Input() set editMode(isEditMode: boolean) {
    this.editModeConfig = isEditMode ? this.DISPLAY_MODE.EDIT : this.DISPLAY_MODE.DEFAULT;
    this.setHeading(isEditMode ? 'configurator.editAdditionalLicenses' : 'configurator.addLicenses');
  }

  @Input() set forceModal(force: boolean) {
    this.forcedModal = force;
  }

  ngOnInit(): void {
    this.editMode = this.forcedModal ?? false;

    this.variantProduct$ = this.configuratorProductService.getVariant$();
    this.displayProduct$ = this.configuratorProductService.getDisplay$();
    this.variantProductLoaded$ = this.configuratorProductService.isVariantProductLoaded$();
    this.displayProductLoaded$ = this.configuratorProductService.isDisplayProductLoaded$();

    this.dcpCartDecoratorService.priceDataSubject.next({});
    this.backToSolutionPage$ = this.checkBackToSolutionPage();

    this.productConfigurationService.productConfigurationSubject.next([]); // this is an inelegant way to keep the edit cart values updated for CPEA without breaking the logic for non-CPEA products
    this.productConfigurationService.productConfigurationSubject
      .pipe(untilDestroyed(this))
      .subscribe(configuratorFields => this.configuratorFields = configuratorFields);

    this.transactionTypeService.getTransactionTypeByUrlOrOrderEntry(this.cartEntry).pipe(untilDestroyed(this))
      .subscribe(transactionType => {
        this.transactionType = transactionType;
        this.transactionTypeTexts$ = this.transactionTypeService.getTranslations(transactionType.code);
        this.getCommercialModelConfig();
      });

    this.subscribeToProduct();

    this.validateCartEntryService.validateCta(this.variantProduct$, this.cartEntry).pipe(
      untilDestroyed(this),
      first())
      .subscribe(() => {
        },
        (error) => {
          this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
        },
        () => {
          this.finishLoadingAndInitialize();
        }
      );

    this.subscribeToErrorHandlingServiceChanges();
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.reduceConfigureCalls).pipe(untilDestroyed(this))
      .subscribe((enabled) => this.reduceConfigureCallsFeatureToggle = enabled);
    this.messageService.getActiveModalMode().pipe(untilDestroyed(this)).subscribe((value) => {
      this.modalMode = value;
    });
  }

  subscribeToErrorHandlingServiceChanges() {
    this.cartErrorHandlingService.updatedPriceData.pipe(untilDestroyed(this)).subscribe((pd: DcpPriceData) => {
      if (this.reduceConfigureCallsFeatureToggle) {
        this.skipConfigureCall = true;
        this.priceData$.next(pd);
        this.priceData = pd;
      }
    });
  }

  finishLoadingAndInitialize() {
    this.isLoading = false;
    this.handleFormChanges();
    this.subscribeToVariantForQuantityData();
    this.cdr.detectChanges();
    if (!this.entitlements$) {
      this.getEntitlementsData(this.cartEntry);
    }
  }

  /**
   * Fetch product code by material number
   * If cart entry is not present (product is not in the cart) then set variant product using fetched product code
   * Set display product
   */
  subscribeToProduct() {
    const supplierAlternativeAID = this.getSupplierAlternativeAID();
    this.dcpProductService.getProductCodesBySupplierAlternativeAid(supplierAlternativeAID)
      .pipe(untilDestroyed(this))
      .subscribe((data: ProductCodes) => {
        if (!this.cartEntry) {
          // if cartEntry exists, its setter passed the product to the ConfiguratorProductService
          this.configuratorProductService.setVariantProduct(data.code);
        }
        this.handleCartEntryChanges(data.code);
        this.configuratorProductService.setDisplayProduct(data.displayProductCode);
        this.waitForNewDisplayProductToBeLoaded();
      }, (e) => {
        this.cartErrorHandlingService.handle(e, null);
      });
  }

  /**
   * Gets the materialNumber of the variant product either from the url or the variant product itself.
   */
  getSupplierAlternativeAID(): string {
    return this.cartEntry?.product?.supplierAlternativeAID || this.uriParamService.getProduct();
  }

  subscribeToVariantForQuantityData() {
    this.variantProduct$.pipe(untilDestroyed(this)).subscribe(variantProduct => {
      this.nameAndSupplierAlternativeAid = this.parseNameAndSupplierAlternativeAid(variantProduct);
      this.productConfigurationService.setUpConfiguration(this.cartEntry);

      combineLatest([this.dcpCartDecoratorService.isProductCpea(), this.priceData$])
        .pipe(first(), untilDestroyed(this))
        .subscribe(([isProductCpea, priceData]) => {
          if (isProductCpea) {
            this.productConfigurationService.setUpConfiguration(this.cartEntry, priceData);
            if (Number(priceData.price?.minimumSpendAmount) > 0) {
              let newSpendAmount: number = Number(priceData.price.minimumSpendAmount);
              if (this.cartEntry) {
                const previousAmount = this.dcpOrderEntryService.getConfiguration(this.cartEntry).spendAmount;
                if (previousAmount > newSpendAmount) {
                  newSpendAmount = previousAmount;
                }
              }
              this.configuratorModel = {
                ...this.configuratorModel,
                spendAmount: newSpendAmount
              };
            }
          }
        });
    });
  }

  handleCartEntryChanges(productCode: string) {
    this.getEntryConfiguration$(productCode).pipe(
      distinctUntilChanged(isEqual),
      untilDestroyed(this),
    ).subscribe((config) => {
      this.configuratorModel = config;
      this.isConfiguratorModelSet$.next(true);
      this.cdr.detectChanges();
    });
  }

  getCommercialModelConfig() {
    this.variantProduct$.pipe(untilDestroyed(this)).subscribe(variantProduct => {
      this.dcpConfigService.getCommercialModelConfig(variantProduct, this.transactionType.code)
        .pipe(untilDestroyed(this)).subscribe((config: CommercialModelConfig) => {
        this.commercialModelConfig = config;
        this.infoMessages = config.messages;
      });
    });
  }

  waitForNewDisplayProductToBeLoaded(): void {
    this.configuratorProductService.isDisplayProductLoaded$()
      .pipe(untilDestroyed(this))
      .subscribe((isLoaded) => {
        this.finishedLoading.emit(isLoaded);
      });
  }

  /**
   * Searches the product in the cart and returns its cart entry configuration
   * when it's found in the cart.
   * Otherwise, quantity: 1
   *
   * @param code product variant code.
   */
  getEntryConfiguration$(code: string): Observable<DcpCartConfigurationForm> {
    return this.dcpCartDecoratorService.getCartConfigurationFormForProduct(code).pipe(concatMap(config => {
      if (config?.entryNumber !== undefined) {
        // Cart exists and this product is in it
        this.editMode = true;
        return of(config);
      }
      return of({
        quantity: 1
      });
    }));
  }

  /**
   * Listen to form changes. Call 'dcpCartDecoratorService' and set new priceData
   */
  handleFormChanges() {
    this.configuratorForm.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(isEqual),
      // after the first /configure call, filter calls with invalid form data
      filter(() => (this.configuratorForm.valid || !this.formInitialized)),
      untilDestroyed(this)
    ).subscribe(() => {
      this.getPriceData();
    });
  }

  getPriceData(): void {
    combineLatest([
      this.variantProduct$,
      this.isConfiguratorModelSet$,
    ]).pipe(first(), untilDestroyed(this)).subscribe(([variantProduct, isConfiguratorModelSet]) => {
      if (isConfiguratorModelSet && !this.canSkipConfigureCall()) {
        this.priceData = null;
        this.cdr.detectChanges();

        // creating deep-copy of configuratorModel to prevent formChanges during request prep causing call to fail
        this.dcpCartDecoratorService.cartConfigure(variantProduct, cloneDeep(this.configuratorModel))
          .pipe(untilDestroyed(this))
          .subscribe(
            (priceData) => {
              const contractCurrency: string = priceData?.configuration?.contract?.currency;
              if (contractCurrency && contractCurrency !== this.dcpCartDecoratorService.selectedCurrency) {
                this.getPriceData();
                return;
              }
              this.priceData = priceData;
              this.priceData$.next(priceData);
              this.cdr.detectChanges();
              this.formInitialized = true;
            }, (e) => {
              // do nothing, error is handled by DcpCartDecoratorService
              this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
            }
          );
      }
    });
  }

  canSkipConfigureCall(): boolean {
    this.configureCallsCount++;
    if (!this.reduceConfigureCallsFeatureToggle) {
      return false;
    }
    // after first call configurator data is up-to-date, form changes can be ignored
    // if product is already in cart then we don't skip any call
    if (this.configureCallsCount === this.CONFIGURE_CALL_AFTER_INIT && !this.isAlreadyInCart()) {
      return true;
    }
    // when error handling service change data then it is up-to-date and call can be skipped
    if (this.skipConfigureCall) {
      this.skipConfigureCall = false;
      return true;
    }
    return false;
  }

  isAlreadyInCart() {
    return this._currentCartEntry;
  }

  addedToCartInner() {
    this.addedToCart.emit();
  }

  multiCurrencyCartAdded(errorInfo: MultiCurrencyErrorInfo) {
    this.multiCurrencyCartError.emit(errorInfo);
    if (this.modalMode) {
      this.setHeader.emit(this.heading);
    }
  }

  setHeading(translationKey: string) {
    if (translationKey) {
      this.translationService.translate(translationKey).pipe(first(), untilDestroyed(this)).subscribe((heading) => {
        this.heading = heading;
      });
    }
  }

  parseNameAndSupplierAlternativeAid(product: Product): string {
    if (!product.supplierAlternativeAID) {
      product.supplierAlternativeAID = this.getSupplierAlternativeAID();
    }
    return this.productUtils.getNameAndSupplierAlternativeAid(product);
  }

  ngOnDestroy(): void {
    this.finishedLoading.emit(false);
    this.configuratorProductService.resetVariantProductSubject();
  }

  checkBackToSolutionPage(): Observable<boolean> {
    return this.route.queryParams.pipe(
      map(params => params.back === '1')
    );
  }

  backToPdp(): void {
    this.router.navigateByUrl(this.pdpHistoryService.getCurrent() || '/');
  }

  public setProcessingState(state: boolean): void {
    this.processingState = state;
    this.cdr.detectChanges();
  }

  public getProcessingState(): boolean {
    return this.processingState;
  }

  private getEntitlementsData(orderEntry: OrderEntry) {
    this.entitlements$ = this.entitlementService.getEntitlement(orderEntry);
    this.entitlements$.pipe(untilDestroyed(this)).subscribe();
    this.tenantKeys = DcpEntitlementService.getTenantKeys();
  }
}
